<template>
  <v-container
    id="planning-view"
    fluid
    tag="section"
  >
    <cumulus-edit-dialog
      ref="editUserTraining"
      v-model="dialogEditUserTraining"
      icon="$edit"
      :title="$t('training_informations')"
      @save="saveDialogEditUserTraining"
      @escape="closeDialogEditUserTraining"
    >
      <template v-slot:editform>
        <edit-user-training
          :user-training="editedUserTraining"
        />
      </template>
    </cumulus-edit-dialog>

    <material-card
      icon="$user_training"
      icon-small
      color="accent"
      :title="page_title"
    >
      <v-card-title>
        <v-row class="ma-0">
          <v-btn
            v-show="editor"
            v-shortkey.once="['f2']"
            color="primary"
            :disabled="!editor"
            class="mb-2 ml-2"
            @shortkey="showDialogEditUserTraining()"
            @click="showDialogEditUserTraining()"
          >
            {{ $t('new_item',{ item: $t('training') }) }}
          </v-btn>
          <v-switch
            v-model="show_closed"
            class="ml-2"
            :label="$t('show_closed')"
          />
          <v-switch
            v-model="show_participants"
            class="ml-2"
            :label="$t('show_participants')"
          />
          <date-field
            v-if="show_participants"
            v-model="operating_date"
            field-name="operating_day"
            hide-details
            class-self="mx-1"
          />
          <v-spacer v-if="!$vuetify.breakpoint.mdAndDown" />
          <v-text-field
            v-model="search"
            append-icon="$search"
            class="ml-auto"
            hide-details
            :label="$t('search_records')"
            single-line
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="computed_headers"
          :items="filtered_items"
          :loading="loading"
          :loading-text="$t('loading')"
          :footer-props="footerProps"
          :item-class="rowClasses"
          :search="search"
          show-expand
          :single-expand="singleExpand"
          :expanded.sync="expanded"
        >
          <template #item.training_name="{item}">
            {{ item.training_name }} <h5> {{ item.comment }} </h5>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td
              :colspan="headers.length"
              class="ma-3 pa-6"
            >
              <v-row>
                <v-col cols="12" md="10">
                  <template v-if="item.success">
                    {{ item.success_date }}
                  </template>
                  <v-btn
                    color="primary"
                    class="ma-2"
                    @click="getReport({ type: 'user-training-report', user_training_id: item.id, source_name: item.training_name + '-' + item.name})"
                  >
                    {{ $t('download_user_training_report') }}
                  </v-btn>
                  <v-btn
                    v-if="profile.can['training-edit-admin']"
                    color="orange"
                    class="ma-2"
                    @click="clickTrainingManagerCheckLog({ type: 'user-training-report', user_training_id: item.id, source_name: item.training_name + '-' + item.name})"
                  >
                    {{ $t('training_manager_check') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" md="1">
                  <tooltip-btn
                    v-if="item.open"
                    color="black"
                    icon="$lock_open"
                    tooltip="tooltip_close_training"
                    @click="clickCloseTraining(item)"
                  />
                  <tooltip-btn
                    v-if="!item.open"
                    color="black"
                    icon="$lock"
                    tooltip="tooltip_open_training"
                    @click="clickOpenTraining(item)"
                  />
                  <tooltip-btn
                    v-if="item.open && !item.success"
                    color="blue"
                    icon="$success"
                    tooltip="tooltip_success_training"
                    @click="clickSuccessTraining(item)"
                  />
                </v-col>
                <v-col cols="12" md="1">
                  <question-btn
                    color="red"
                    icon="$delete"
                    tooltip="tooltip_delete_training"
                    question="user_training_delete_question"
                    question-description="user_training_delete_description"
                    @click="clickDeleteTraining(item)"
                  />
                </v-col>
              </v-row>
            </td>
          </template>

          <template #item.actions="{item}">
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="!action.disable"
              :color="action.color"
              :icon="action.icon"
              :tooltip="action.tooltip"
              :to="action.to ? action.to+item.id : ''"
              @click="action.func(item)"
            >
              <v-icon v-text="action.icon" />
            </tooltip-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync, dispatch } from 'vuex-pathify'
  import EditUserTraining from '@/layouts/default/edit/UserTraining'
  import QuestionBtn from '@/components/QuestionBtn'
  import { format } from 'date-fns'

  export default {
    name: 'Airports',
    components: {
      QuestionBtn,
      EditUserTraining,
    },
    data () {
      return {
        items: [],
        expanded: [],
        singleExpand: true,
        show_closed: false,
        show_participants: false,
        operating_date: null,
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        country: 'Hungary',
        total: 0,
        timer: null,
        dialogEditUserTraining: false,
        editedUserTraining: {},
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      search: sync('userTraining/search'),
      table_headers () {
        return [
          {
            text: this.$t('fields.name'),
            align: 'start',
            sortable: false,
            value: 'name',
          },
          {
            hide_small: true,
            text: this.$t('training_name'),
            sortable: false,
            value: 'training_name',
          },
          {
            hide_small: true,
            text: this.$t('fields.last_task'),
            sortable: false,
            value: 'last_task.idx',
          },
          {
            hide_small: true,
            text: this.$t('fields.status'),
            sortable: false,
            value: 'last_task.status',
          },
          {
            text: this.$t('fields.last_flight_time'),
            sortable: false,
            value: 'last_flight_time',
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          // { color: 'info', icon: '$info' },
          { color: 'blue', icon: '$documents', tooltip: 'tooltip_documents', to: '/documents/user_training/' },
          { color: 'info', icon: '$list', tooltip: 'tooltip_edit', func: this.edit, disable: !this.editor },
        ]
      },
      page_title () { return this.$t('planning') },
      editor () { return this.profile.can['training-edit-instructor'] },
      computed_headers () { return this.table_headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
      filtered_items () { return this.items.filter(i => i.open || this.show_closed) },
    },
    watch: {
      show_participants: {
        handler () {
          this.load()
        },
        deep: true,
      },
      operating_date: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      this.operating_date = format(new Date(), 'yyyy-MM-dd')
      this.load()
    },
    methods: {
      getReport: call('action/getReport'),
      getList: call('userTraining/search_planning'),
      openTraining: call('userTraining/open'),
      closeTraining: call('userTraining/close'),
      deleteTraining: call('userTraining/delete'),
      addUserTraining: call('userTraining/insert_user_training'),
      successTraining: call('userTraining/success'),
      trainingManagerCheckLog: call('userTraining/training_manager_check_log'),
      async load () {
        try {
          await this.getList({
            show_participants: this.show_participants,
            operating_date: this.operating_date,
          }).then(response => {
            this.items = response
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (item) {
        this.$router.push(`/training/edit/${item.user_id}/${item.id}`).catch(() => {})
      },
      add () {
      },
      showDialogEditUserTraining (item) {
        this.editedUserTraining = {}
        this.dialogEditUserTraining = true
      },
      closeDialogEditUserTraining () {
        this.editedUserTraining = {}
        this.dialogEditUserTraining = false
        this.load()
      },
      async saveDialogEditUserTraining () {
        await this.addUserTraining(this.editedUserTraining).then(response => {
          this.closeDialogEditUserTraining()
        }).catch(e => {
          this.$refs.editUserTraining.setErrors(e)
        })
      },
      clickCloseTraining (item) {
        this.closeTraining(item).then(response => {
          item.open = false
        })
      },
      clickDeleteTraining (item) {
        this.deleteTraining(
          { id: item.id },
        ).then(response => {
          this.items.splice(this.items.indexOf(item), 1)
        }).catch(e => {
          dispatch('alert/setSnackbar', { text: e, type: 'error' })
        })
      },
      clickOpenTraining (item) {
        this.openTraining(item).then(response => {
          item.open = true
          item.success = false
          item.success_date = null
        })
      },
      clickSuccessTraining (item) {
        this.successTraining(item).then(response => {
          item.success = true
          item.success_date = item.success_date ?? format(new Date(), 'yyyy-MM-dd')
          item.open = false
        }).catch(e => {
          dispatch('alert/setSnackbar', { text: e, type: 'error' })
        })
      },
      clickTrainingManagerCheckLog (item) {
        this.trainingManagerCheckLog(item).then(response => {
          this.getReport(item)
        }).catch(e => {
          dispatch('alert/setSnackbar', { text: e, type: 'error' })
        })
      },
      rowClasses (item) {
        if (item.success_date !== null) {
          return 'green lighten-5'
        } else if (item.open) {
          return ''
        } else {
          return 'red lighten-5'
        }
      },
    },
  }
</script>
